import React, { useState, useMemo, useRef } from 'react';
import { Plus, Pencil, Trash2, Check, X, Search, ChevronDown, Upload, Download } from 'lucide-react';
import { useData } from '../contexts/DataContext';

const MARKETS = ['SE', 'NO', 'FI', 'DE', 'DK', 'NL'];
const FLAGS = { SE: '🇸🇪', NO: '🇳🇴', FI: '🇫🇮', DE: '🇩🇪', DK: '🇩🇰', NL: '🇳🇱' };
const CATEGORIES = ['Color', 'Attribute', 'Medical Term', 'Brand Term'];
const STATUSES = ['Approved', 'Pending'];
const CSV_HEADERS = ['Term', 'Category', 'Collection', 'SE', 'NO', 'FI', 'DE', 'DK', 'NL', 'Status', 'Notes'];

// CSV helpers
function entriesToCsv(entries) {
  const rows = [CSV_HEADERS.join(',')];
  for (const e of entries) {
    const vals = [
      e.termSE || '', e.category || '', e.collection || 'Standard',
      e.translations?.SE || '', e.translations?.NO || '',
      e.translations?.FI || '', e.translations?.DE || '',
      e.translations?.DK || '', e.translations?.NL || '',
      e.status || 'Pending', e.notes || '',
    ].map(v => `"${String(v).replace(/"/g, '""')}"`);
    rows.push(vals.join(','));
  }
  return rows.join('\n');
}

function parseCsvToEntries(text) {
  const lines = text.trim().split('\n').filter(Boolean);
  if (lines.length < 2) throw new Error('Filen är tom eller saknar datarader.');
  const header = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim());
  const idxOf = (name) => header.findIndex(h => h.toLowerCase() === name.toLowerCase());
  const iT = idxOf('Term'), iCat = idxOf('Category'), iCol = idxOf('Collection'),
    iSE = idxOf('SE'), iNO = idxOf('NO'), iFI = idxOf('FI'),
    iDE = idxOf('DE'), iDK = idxOf('DK'), iNL = idxOf('NL'),
    iSt = idxOf('Status'), iN = idxOf('Notes');
  if (iT === -1) throw new Error('Kolumn "Term" saknas i CSV-filen.');
  const entries = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].match(/("(?:[^"]|"")*"|[^,]*)/g)?.map(c => c.replace(/^"|"$/g, '').replace(/""/g, '"').trim()) || [];
    const term = cols[iT];
    if (!term) continue;
    entries.push({
      id: Date.now() + i,
      createdAt: new Date().toISOString(),
      termSE: term,
      category: (iCat >= 0 && cols[iCat]) || 'Medical Term',
      collection: (iCol >= 0 && cols[iCol]) || 'Standard',
      status: (iSt >= 0 && ['Approved','Pending'].includes(cols[iSt])) ? cols[iSt] : 'Pending',
      notes: iN >= 0 ? cols[iN] || '' : '',
      translations: {
        SE: iSE >= 0 ? cols[iSE] || '' : '',
        NO: iNO >= 0 ? cols[iNO] || '' : '',
        FI: iFI >= 0 ? cols[iFI] || '' : '',
        DE: iDE >= 0 ? cols[iDE] || '' : '',
        DK: iDK >= 0 ? cols[iDK] || '' : '',
        NL: iNL >= 0 ? cols[iNL] || '' : '',
      },
    });
  }
  return entries;
}

const EMPTY_FORM = {
  termSE: '', category: 'Medical Term', collection: 'Standard', status: 'Pending', notes: '',
  translations: { SE: '', NO: '', FI: '', DE: '', DK: '', NL: '' },
};

function EntryForm({ initial = EMPTY_FORM, onSave, onCancel, collections = ['Standard'] }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setTr = (market, v) => setForm(f => ({ ...f, translations: { ...f.translations, [market]: v } }));

  return (
    <div className="vocab-form" data-testid="vocab-form">
      <div className="vocab-form-row">
        <div className="vocab-form-group vocab-form-group--wide">
          <label className="vocab-form-label">Källterm (SE)</label>
          <input className="vocab-form-input" value={form.termSE}
            onChange={e => set('termSE', e.target.value)}
            placeholder="t.ex. kompressionsstrumpa" data-testid="term-se-input" />
        </div>
        <div className="vocab-form-group">
          <label className="vocab-form-label">Kategori</label>
          <div className="vocab-select-wrap">
            <select className="vocab-form-select" value={form.category}
              onChange={e => set('category', e.target.value)} data-testid="category-select">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <ChevronDown size={13} className="vocab-select-icon" />
          </div>
        </div>
        <div className="vocab-form-group">
          <label className="vocab-form-label">Samling</label>
          <div className="vocab-select-wrap">
            <select className="vocab-form-select" value={form.collection || 'Standard'}
              onChange={e => set('collection', e.target.value)} data-testid="collection-select">
              {collections.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <ChevronDown size={13} className="vocab-select-icon" />
          </div>
        </div>
        <div className="vocab-form-group">
          <label className="vocab-form-label">Status</label>
          <div className="vocab-select-wrap">
            <select className="vocab-form-select" value={form.status}
              onChange={e => set('status', e.target.value)} data-testid="status-select">
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown size={13} className="vocab-select-icon" />
          </div>
        </div>
      </div>

      <div className="vocab-form-row vocab-translations-row">
        {MARKETS.map(m => (
          <div key={m} className="vocab-form-group">
            <label className="vocab-form-label">{FLAGS[m]} {m}</label>
            <input className="vocab-form-input" value={form.translations[m] || ''}
              onChange={e => setTr(m, e.target.value)}
              placeholder={`Term på ${m}`} data-testid={`translation-${m}-input`} />
          </div>
        ))}
      </div>

      <div className="vocab-form-row">
        <div className="vocab-form-group vocab-form-group--wide">
          <label className="vocab-form-label">Anteckning <span className="vocab-optional">(valfritt)</span></label>
          <input className="vocab-form-input" value={form.notes}
            onChange={e => set('notes', e.target.value)}
            placeholder="t.ex. Används för alla kompressionsprodukter" data-testid="notes-input" />
        </div>
        <div className="vocab-form-actions">
          <button className="vocab-btn vocab-btn--ghost" onClick={onCancel} data-testid="cancel-form-btn">
            <X size={14} /> Avbryt
          </button>
          <button className="vocab-btn vocab-btn--primary"
            onClick={() => form.termSE.trim() && onSave(form)}
            disabled={!form.termSE.trim()} data-testid="save-entry-btn">
            <Check size={14} /> Spara
          </button>
        </div>
      </div>
    </div>
  );
}

export default function VocabularyPage() {
  const csvRef = useRef(null);
  const data = useData();
  const vocab = data.vocabulary;
  const vocabCollections = data.vocabCollections || ['Standard'];

  // Collection tab state
  const [activeTab, setActiveTab] = useState('');
  const [showNewColInput, setShowNewColInput] = useState(false);
  const [newColName, setNewColName] = useState('');

  // Entry form state
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  // Filter state
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Import/export state
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');

  // ── Collection management ────────────────────────────────────────────────────
  const handleAddCollection = () => {
    const name = newColName.trim();
    if (!name || vocabCollections.includes(name)) {
      setShowNewColInput(false);
      setNewColName('');
      return;
    }
    data.saveVocabCollections([...vocabCollections, name]);
    setActiveTab(name);
    setShowNewColInput(false);
    setNewColName('');
  };

  const handleDeleteCollection = (colName) => {
    if (colName === 'Standard') return;
    if (!window.confirm(`Ta bort samlingen "${colName}"? Alla termer i samlingen flyttas till "Standard".`)) return;
    const updated = vocab.map(e => (e.collection || 'Standard') === colName ? { ...e, collection: 'Standard' } : e);
    data.saveVocabulary(updated);
    data.saveVocabCollections(vocabCollections.filter(c => c !== colName));
    setActiveTab('');
  };

  // ── Entry CRUD ────────────────────────────────────────────────────────────────
  const handleAdd = (form) => {
    data.addVocabEntry({ ...form, collection: form.collection || activeTab || 'Standard' });
    setShowForm(false);
  };

  const handleUpdate = (form) => {
    data.updateVocabEntry(editId, form);
    setEditId(null);
  };

  const handleDelete = (id) => {
    if (window.confirm('Ta bort denna term?')) {
      data.deleteVocabEntry(id);
    }
  };

  // ── Import / Export ────────────────────────────────────────────────────────────
  const handleExport = () => {
    const toExport = activeTab ? vocab.filter(e => (e.collection || 'Standard') === activeTab) : vocab;
    const csv = entriesToCsv(toExport);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `vardvaskan-vocabulary${activeTab ? `-${activeTab}` : ''}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const handleImportClick = () => { setImportError(''); setImportSuccess(''); csvRef.current?.click(); };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const entries = parseCsvToEntries(ev.target.result);
        if (!entries.length) { setImportError('Inga giltiga rader hittades i filen.'); return; }
        const existing = vocab;
        const existingTerms = new Set(existing.map(e => e.termSE?.toLowerCase()));
        const newEntries = entries.filter(e => !existingTerms.has(e.termSE?.toLowerCase()));
        const merged = [...existing, ...newEntries];
        data.saveVocabulary(merged);
        // Merge any new collections from imported file
        const importedCols = [...new Set(newEntries.map(e => e.collection || 'Standard'))];
        const missingCols = importedCols.filter(c => !vocabCollections.includes(c));
        if (missingCols.length > 0) {
          data.saveVocabCollections([...vocabCollections, ...missingCols]);
        }
        setImportSuccess(`${newEntries.length} termer importerade. ${entries.length - newEntries.length} duplikat hoppades över.`);
      } catch (err) { setImportError(err.message); }
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  // ── Filtered entries ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return vocab.filter(e => {
      if (activeTab && (e.collection || 'Standard') !== activeTab) return false;
      if (search && !e.termSE?.toLowerCase().includes(search.toLowerCase()) &&
          !Object.values(e.translations || {}).some(t => t?.toLowerCase().includes(search.toLowerCase()))) return false;
      if (filterCat && e.category !== filterCat) return false;
      if (filterStatus && e.status !== filterStatus) return false;
      return true;
    });
  }, [vocab, activeTab, search, filterCat, filterStatus]);

  const approvedCount = vocab.filter(e => e.status === 'Approved').length;
  const displayCount = activeTab ? vocab.filter(e => (e.collection || 'Standard') === activeTab).length : vocab.length;

  return (
    <div className="page-scroll">
      <div className="page-header">
        <div>
          <h1 className="page-title">Vokabulär</h1>
          <p className="page-subtitle">
            Terminologikrav som injiceras automatiskt i alla genereringsprompts.
            {' '}<strong>{approvedCount} godkända</strong> termer aktiva.
          </p>
        </div>
        <div className="vocab-header-actions">
          <button className="vocab-btn vocab-btn--ghost" onClick={handleImportClick} data-testid="import-csv-btn">
            <Upload size={14} /> Importera CSV
          </button>
          <button className="vocab-btn vocab-btn--ghost" onClick={handleExport} disabled={!vocab.length} data-testid="export-csv-btn">
            <Download size={14} /> Exportera CSV
          </button>
          <button className="vocab-btn vocab-btn--primary"
            onClick={() => { setShowForm(true); setEditId(null); }}
            data-testid="add-entry-btn">
            <Plus size={15} /> Lägg till term
          </button>
        </div>
        <input ref={csvRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportFile} data-testid="csv-file-input" />
      </div>

      {importError && <div className="vocab-import-msg vocab-import-msg--error" data-testid="import-error">{importError}</div>}
      {importSuccess && <div className="vocab-import-msg vocab-import-msg--success" data-testid="import-success">{importSuccess}</div>}

      {/* Collection Tabs */}
      <div className="vocab-collections-tabs" data-testid="vocab-collections-tabs">
        <button
          className={`vocab-col-tab${activeTab === '' ? ' vocab-col-tab--active' : ''}`}
          onClick={() => setActiveTab('')}
          data-testid="tab-all"
        >
          Alla ({vocab.length})
        </button>
        {vocabCollections.map(col => {
          const count = vocab.filter(e => (e.collection || 'Standard') === col).length;
          return (
            <div key={col} className="vocab-col-tab-wrap">
              <button
                className={`vocab-col-tab${activeTab === col ? ' vocab-col-tab--active' : ''}`}
                onClick={() => setActiveTab(col)}
                data-testid={`tab-${col}`}
              >
                {col} ({count})
              </button>
              {activeTab === col && col !== 'Standard' && (
                <button
                  className="vocab-col-tab-delete"
                  onClick={() => handleDeleteCollection(col)}
                  title={`Ta bort samling "${col}"`}
                  data-testid={`delete-col-${col}`}
                >
                  <X size={11} />
                </button>
              )}
            </div>
          );
        })}
        {showNewColInput ? (
          <div className="vocab-new-col-wrap">
            <input
              className="vocab-new-col-input"
              value={newColName}
              onChange={e => setNewColName(e.target.value)}
              placeholder="Namn på samling…"
              autoFocus
              data-testid="new-col-input"
              onKeyDown={e => {
                if (e.key === 'Enter') handleAddCollection();
                if (e.key === 'Escape') { setShowNewColInput(false); setNewColName(''); }
              }}
            />
            <button className="vocab-btn vocab-btn--primary vocab-btn--sm" onClick={handleAddCollection} data-testid="confirm-new-col-btn">OK</button>
            <button className="vocab-btn vocab-btn--ghost vocab-btn--sm" onClick={() => { setShowNewColInput(false); setNewColName(''); }}>Avbryt</button>
          </div>
        ) : (
          <button className="vocab-col-tab vocab-col-tab--add" onClick={() => setShowNewColInput(true)} data-testid="add-col-btn">
            + Ny samling
          </button>
        )}
      </div>

      {showForm && !editId && (
        <EntryForm
          initial={{ ...EMPTY_FORM, collection: activeTab || 'Standard' }}
          onSave={handleAdd}
          onCancel={() => setShowForm(false)}
          collections={vocabCollections}
        />
      )}

      {/* Filters */}
      <div className="vocab-filters" data-testid="vocab-filters">
        <div className="vocab-search-wrap">
          <Search size={14} className="vocab-search-icon" />
          <input className="vocab-search" placeholder="Sök term…"
            value={search} onChange={e => setSearch(e.target.value)} data-testid="vocab-search" />
        </div>
        <div className="vocab-select-wrap">
          <select className="vocab-filter-select" value={filterCat}
            onChange={e => setFilterCat(e.target.value)} data-testid="filter-category">
            <option value="">Alla kategorier</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <ChevronDown size={13} className="vocab-select-icon" />
        </div>
        <div className="vocab-select-wrap">
          <select className="vocab-filter-select" value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)} data-testid="filter-status">
            <option value="">Alla statusar</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <ChevronDown size={13} className="vocab-select-icon" />
        </div>
        <span className="vocab-count">{filtered.length} {filtered.length === 1 ? 'term' : 'termer'}</span>
      </div>

      {/* Table */}
      <div className="vocab-table-wrap">
        <table className="vocab-table" data-testid="vocab-table">
          <thead>
            <tr>
              <th>Term (SE)</th>
              <th>Kategori</th>
              <th>Samling</th>
              <th>Översättningar</th>
              <th>Status</th>
              <th>Anteckning</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="vocab-empty">
                {vocab.length === 0 ? 'Inga termer ännu — lägg till din första term ovan.' : 'Inga termer matchar filtret.'}
              </td></tr>
            )}
            {filtered.map(entry => (
              editId === entry.id ? (
                <tr key={entry.id} className="vocab-edit-row">
                  <td colSpan={7} style={{ padding: 0 }}>
                    <EntryForm
                      initial={entry}
                      onSave={handleUpdate}
                      onCancel={() => setEditId(null)}
                      collections={vocabCollections}
                    />
                  </td>
                </tr>
              ) : (
                <tr key={entry.id} data-testid={`vocab-row-${entry.id}`}>
                  <td className="vocab-term-cell">
                    <strong>{entry.termSE}</strong>
                  </td>
                  <td>
                    <span className={`vocab-category-badge vocab-cat--${entry.category?.replace(/\s+/g, '-').toLowerCase()}`}>
                      {entry.category}
                    </span>
                  </td>
                  <td>
                    <span className="vocab-collection-badge" data-testid={`collection-badge-${entry.id}`}>
                      {entry.collection || 'Standard'}
                    </span>
                  </td>
                  <td className="vocab-translations-cell">
                    {MARKETS.map(m => {
                      const t = entry.translations?.[m];
                      return t ? (
                        <span key={m} className="vocab-translation-chip" title={`${FLAGS[m]} ${m}: ${t}`}>
                          {FLAGS[m]} <em>{t}</em>
                        </span>
                      ) : (
                        <span key={m} className="vocab-translation-chip vocab-translation-chip--empty" title={`${m}: saknas`}>
                          {FLAGS[m]}
                        </span>
                      );
                    })}
                  </td>
                  <td>
                    <span className={`vocab-status-badge${entry.status === 'Approved' ? ' vocab-status--approved' : ' vocab-status--pending'}`}
                      data-testid={`status-badge-${entry.id}`}>
                      {entry.status === 'Approved' ? <Check size={11} /> : null}
                      {entry.status}
                    </span>
                  </td>
                  <td className="vocab-notes-cell">{entry.notes || '—'}</td>
                  <td className="vocab-actions-cell">
                    <button className="vocab-icon-btn" onClick={() => { setEditId(entry.id); setShowForm(false); }}
                      title="Redigera" data-testid={`edit-btn-${entry.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button className="vocab-icon-btn vocab-icon-btn--danger" onClick={() => handleDelete(entry.id)}
                      title="Ta bort" data-testid={`delete-btn-${entry.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
